import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { TokenState, useGetAllClientInvitations } from '@/gen/publicApi'

import Breadcrumbs from '@/containers/breadcrumbs'
import { UnexpectedDataStateError } from '@/containers/unexpectedDataStateError'

import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { Card } from '@/components/card'
import Content from '@/components/content'
import CopyIcon from '@/components/copyIcon'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/dialog'
import PageHeader from '@/components/pageHeader'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/table'

import { useSetBreadcrumbs } from '@/state/breadcrumbs'

import { inviteWebUrl } from '@/utils/invitations.ts'
import { fromUtc } from '@/utils/time'

import SettingsNavigation from '../-components/settingsNavigation'

import EmptyInvitations from './-components/empty'
import InviteSomeone from './-components/inviteSomeoneForm'
import RevokeInvitation from './-components/revokeInvitation'

export const Route = createFileRoute('/_authed/$client_code/settings/invitations/')({
    component: Invitations,
})

function Invitations() {
    const [createDialogOpen, setCreateDialogOpen] = useState(false)
    const { client_code } = Route.useParams()
    // TODO - add pagination (Issue #206)
    const { data: invitations, refetch } = useGetAllClientInvitations(client_code, undefined, {
        query: { suspense: true },
    })
    useSetBreadcrumbs([
        {
            to: '/$client_code/settings',
            text: 'Settings',
            params: { client_code },
        },
        'seperator',
        {
            to: '/$client_code/invitations',
            text: 'Invitations',
            params: { client_code },
        },
    ])

    if (!invitations?.data) {
        return <UnexpectedDataStateError />
    }

    const onDialogChange = (open: boolean) => {
        setCreateDialogOpen(open)
    }

    const onInviteSuccess = () => {
        refetch()
        setCreateDialogOpen(false)
    }

    const onRevokeSuccess = () => {
        refetch()
        setCreateDialogOpen(false)
    }

    return (
        <>
            <Breadcrumbs />
            <Content>
                <PageHeader
                    title="Invitations"
                    controls={
                        <Dialog open={createDialogOpen} onOpenChange={onDialogChange}>
                            <DialogTrigger asChild>
                                <Button variant={'outline'} className="mb-2" onClick={() => setCreateDialogOpen(true)}>
                                    Invite someone new
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Send Invitation</DialogTitle>
                                </DialogHeader>
                                <InviteSomeone onSuccess={onInviteSuccess} />
                            </DialogContent>
                        </Dialog>
                    }
                />
                <SettingsNavigation active="invitations" />

                <Card hideOverflow>
                    {invitations.data.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Expires At</TableHead>
                                    <TableHead>State</TableHead>
                                    <TableHead />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {invitations.data.map(invitation => (
                                    <TableRow key={invitation.id}>
                                        <TableCell>{invitation.email}</TableCell>
                                        <TableCell>
                                            {invitation.tokenState === 'Pending' ? fromUtc(invitation.expiresUtc) : '-'}
                                        </TableCell>

                                        <TableCell>
                                            <InvitationStateBadge state={invitation.tokenState} />
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {invitation.tokenState === 'Pending' && (
                                                <>
                                                    <CopyIcon
                                                        value={inviteWebUrl(invitation.tokenValue)}
                                                        name="invitation link"
                                                        tooltip="Copy invitation url"
                                                    />
                                                    <RevokeInvitation
                                                        clientCode={client_code}
                                                        invitation={invitation}
                                                        onSuccess={onRevokeSuccess}
                                                    />
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <EmptyInvitations onClick={() => setCreateDialogOpen(true)} />
                    )}
                </Card>
            </Content>
        </>
    )
}

interface InvitationStateBadgeProps {
    state?: TokenState
}

function InvitationStateBadge({ state }: InvitationStateBadgeProps) {
    switch (state) {
        case 'Pending':
            return <Badge variant="default">Pending</Badge>
        case 'Accepted':
            return <Badge variant="success">Accepted</Badge>
        case 'Expired':
            return <Badge variant="warning">Expired</Badge>
        case 'Revoked':
            return <Badge variant="destructive">Revoked</Badge>
    }
}
